/**
 * Provider Directory API Service
 * Handles all API calls to the provider directory backend
 * Falls back to mock data when backend is not available
 */

import { Provider } from '@/components/provider-directory/ProviderResults';
import { mockSearchByZip, mockSearchByRadius } from './mockProviderData';
import { config } from '@/src/config';
import { debug } from './debug';

// Use centralized config for API URL (handles environment detection and Vercel proxy)
const API_BASE_URL = config.providerApi.baseUrl;
const USE_MOCK_DATA = import.meta.env.VITE_USE_MOCK_PROVIDERS === 'true'; // Default to real API

// Debug logging (development only via debug utility)
debug.log('Provider', 'API Configuration', {
  API_BASE_URL,
  USE_MOCK_DATA,
  VITE_USE_MOCK_PROVIDERS: import.meta.env.VITE_USE_MOCK_PROVIDERS,
  environment: config.environment
});

interface SearchFilters {
  organizationType?: string;
  services?: string[];
  acceptsMedicaid?: boolean;
  acceptsMedicare?: boolean;
  languagesSpoken?: string[];
  minQualityScore?: number;
}

interface GeocodeResult {
  latitude: number;
  longitude: number;
  formatted_address: string;
}

interface SearchResponse {
  providers: Provider[];
  total_count: number;
  search_time_ms: number;
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Fetch with retry and exponential backoff. Retries on network errors and 5xx responses,
 * which covers Render edge 500s that can occur during deploys or worker recycles
 * (these responses arrive without CORS headers and surface as TypeError in the browser).
 */
async function fetchWithRetry(
  input: RequestInfo,
  init?: RequestInit,
  { retries = 3, backoffMs = 1000 }: { retries?: number; backoffMs?: number } = {}
): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(input, init);
      if (response.ok || response.status < 500) return response;
      lastError = new Error(`HTTP ${response.status}: ${response.statusText}`);
    } catch (err) {
      lastError = err;
    }
    if (attempt < retries) {
      await sleep(backoffMs * Math.pow(2, attempt));
    }
  }
  throw lastError instanceof Error ? lastError : new Error('Request failed');
}

class ProviderDirectoryAPI {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Geocode an address string to coordinates
   */
  async geocodeAddress(address: string): Promise<GeocodeResult> {
    try {
      debug.apiRequest('POST', `${this.baseUrl}/geocode`, { body: { address } });
      const response = await fetch(`${this.baseUrl}/geocode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ address }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        debug.apiError(response.status, { message: 'Geocoding failed', errorText });
        throw new Error(`Geocoding failed: ${response.statusText}`);
      }

      const result = await response.json();
      debug.apiResponse(response.status, true, result);
      return result;
    } catch (error) {
      debug.error('Provider', 'Geocoding error', error);
      throw new Error('Failed to geocode address. Please try a different location.');
    }
  }

  /**
   * Search providers by geographic radius
   */
  async searchByRadius(
    latitude: number,
    longitude: number,
    radiusMiles: number = 10,
    filters?: SearchFilters
  ): Promise<SearchResponse> {
    // Use mock data if enabled or if backend fails
    if (USE_MOCK_DATA) {
      debug.log('Provider', 'Using mock provider data for radius search');
      return Promise.resolve(mockSearchByRadius(latitude, longitude, radiusMiles, filters));
    }

    try {
      const params = new URLSearchParams({
        latitude: latitude.toString(),
        longitude: longitude.toString(),
        radius_miles: radiusMiles.toString(),
      });

      // Add filters to query params
      if (filters?.organizationType) {
        params.append('organization_type', filters.organizationType);
      }
      if (filters?.acceptsMedicaid) {
        params.append('accepts_medicaid', 'true');
      }
      if (filters?.acceptsMedicare) {
        params.append('accepts_medicare', 'true');
      }
      if (filters?.services && filters.services.length > 0) {
        filters.services.forEach(service => params.append('services', service));
      }
      if (filters?.languagesSpoken && filters.languagesSpoken.length > 0) {
        filters.languagesSpoken.forEach(lang => params.append('languages', lang));
      }
      if (filters?.minQualityScore) {
        params.append('min_quality_score', filters.minQualityScore.toString());
      }

      const response = await fetch(`${this.baseUrl}/search/radius?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      debug.error('Provider', 'Search error, falling back to mock data', error);
      // Fallback to mock data on error
      return mockSearchByRadius(latitude, longitude, radiusMiles, filters);
    }
  }

  /**
   * Search providers by ZIP code
   */
  async searchByZip(
    zipCode: string,
    radiusMiles: number = 10,
    filters?: SearchFilters
  ): Promise<SearchResponse> {
    // Use mock data if enabled or if backend fails
    if (USE_MOCK_DATA) {
      debug.log('Provider', 'Using mock provider data for ZIP code search', { zipCode });
      return Promise.resolve(mockSearchByZip(zipCode, radiusMiles, filters));
    }

    try {
      const params = new URLSearchParams({
        zip_code: zipCode,
        radius_miles: radiusMiles.toString(),
      });

      // Add filters
      if (filters?.organizationType) {
        params.append('organization_type', filters.organizationType);
      }
      if (filters?.acceptsMedicaid) {
        params.append('accepts_medicaid', 'true');
      }
      if (filters?.acceptsMedicare) {
        params.append('accepts_medicare', 'true');
      }
      if (filters?.services && filters.services.length > 0) {
        filters.services.forEach(service => params.append('services', service));
      }

      const response = await fetch(`${this.baseUrl}/search/by-zip?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      debug.error('Provider', 'Search error, falling back to mock data', error);
      // Fallback to mock data on error
      return mockSearchByZip(zipCode, radiusMiles, filters);
    }
  }

  /**
   * Search providers by county
   */
  async searchByCounty(
    state: string,
    county: string,
    filters?: SearchFilters
  ): Promise<SearchResponse> {
    try {
      const params = new URLSearchParams({
        state,
        county,
      });

      // Add filters
      if (filters?.organizationType) {
        params.append('organization_type', filters.organizationType);
      }
      if (filters?.acceptsMedicaid) {
        params.append('accepts_medicaid', 'true');
      }
      if (filters?.acceptsMedicare) {
        params.append('accepts_medicare', 'true');
      }

      const response = await fetch(`${this.baseUrl}/search/by-county?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Search error:', error);
      throw new Error('Failed to search providers by county. Please try again.');
    }
  }

  /**
   * Get a single provider by ID
   */
  async getProvider(providerId: string): Promise<Provider> {
    try {
      const response = await fetch(`${this.baseUrl}/providers/${providerId}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch provider: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Get provider error:', error);
      throw new Error('Failed to fetch provider details. Please try again.');
    }
  }

  /**
   * Get all available services
   */
  async getServices(): Promise<string[]> {
    try {
      const response = await fetch(`${this.baseUrl}/services`);

      if (!response.ok) {
        throw new Error(`Failed to fetch services: ${response.statusText}`);
      }

      const data = await response.json();
      return data.services || [];
    } catch (error) {
      console.error('Get services error:', error);
      return [];
    }
  }

  /**
   * Get all available organization types with counts
   */
  async getOrganizationTypes(): Promise<Array<{type: string, count: number}>> {
    try {
      const response = await fetch(`${this.baseUrl}/providers/organization-types`);

      if (!response.ok) {
        throw new Error(`Failed to fetch organization types: ${response.statusText}`);
      }

      const data = await response.json();
      return data.organization_types || [];
    } catch (error) {
      console.error('Get organization types error:', error);
      // Return fallback types if API fails
      return [
        { type: 'FQHC', count: 0 },
        { type: 'Healthcare Provider', count: 0 },
        { type: 'Mental Health', count: 0 },
        { type: 'Social Services', count: 0 },
        { type: 'Substance Abuse Treatment', count: 0 }
      ];
    }
  }

  /**
   * Submit feedback about a provider
   */
  async submitFeedback(
    providerId: string,
    feedbackType: 'incorrect_info' | 'closed' | 'positive' | 'other',
    comments: string,
    userEmail?: string
  ): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          organization_id: providerId,
          feedback_type: feedbackType,
          comments,
          user_email: userEmail,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit feedback: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Submit feedback error:', error);
      throw new Error('Failed to submit feedback. Please try again.');
    }
  }

  /**
   * Combined search that auto-detects location type
   */
  async search(
    location: string,
    radiusMiles: number = 10,
    filters?: SearchFilters
  ): Promise<SearchResponse> {
    // Check if location is a ZIP code (5 digits)
    const zipPattern = /^\d{5}$/;
    if (zipPattern.test(location.trim())) {
      return this.searchByZip(location.trim(), radiusMiles, filters);
    }

    // Check if location is coordinates (latitude,longitude)
    const coordPattern = /^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/;
    const coordMatch = location.trim().match(coordPattern);
    if (coordMatch) {
      const latitude = parseFloat(coordMatch[1]);
      const longitude = parseFloat(coordMatch[2]);
      debug.log('Provider', 'Using coordinates', { latitude, longitude });
      return this.searchByRadius(latitude, longitude, radiusMiles, filters);
    }

    // Otherwise, geocode the address and search by coordinates
    try {
      debug.log('Provider', 'Geocoding address', { location });
      const geocodeResult = await this.geocodeAddress(location);
      debug.log('Provider', 'Geocoded successfully', geocodeResult);
      return this.searchByRadius(
        geocodeResult.latitude,
        geocodeResult.longitude,
        radiusMiles,
        filters
      );
    } catch (error) {
      debug.error('Provider', 'Search error', error);
      throw error;
    }
  }

  /**
   * Browse providers with pagination (no location required)
   */
  async browse(params: {
    page?: number;
    pageSize?: number;
    organizationType?: string;
    services?: string;
    state?: string;
    category?: string;
    sortBy?: string;
    geocodedOnly?: boolean;
  } = {}): Promise<BrowseResponse> {
    try {
      const urlParams = new URLSearchParams();
      if (params.page) urlParams.append('page', params.page.toString());
      if (params.pageSize) urlParams.append('page_size', params.pageSize.toString());
      if (params.organizationType) urlParams.append('organization_type', params.organizationType);
      if (params.services) urlParams.append('services', params.services);
      if (params.state) urlParams.append('state', params.state);
      if (params.category) urlParams.append('category', params.category);
      if (params.sortBy) urlParams.append('sort_by', params.sortBy);
      if (params.geocodedOnly) urlParams.append('geocoded_only', 'true');

      const response = await fetch(`${this.baseUrl}/providers/browse?${urlParams.toString()}`);

      if (!response.ok) {
        throw new Error(`Browse failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      debug.error('Provider', 'Browse error', error);
      throw new Error('Failed to browse providers. Please try again.');
    }
  }

  /**
   * Get provider categories and taxonomies
   */
  async getCategories(): Promise<CategoriesResponse> {
    try {
      const response = await fetchWithRetry(`${this.baseUrl}/providers/categories`, undefined, {
        retries: 3,
        backoffMs: 1000,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch categories: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      debug.error('Provider', 'Categories error', error);
      throw error instanceof Error ? error : new Error('Failed to load categories');
    }
  }

  /**
   * AI-powered natural language search
   */
  async smartSearch(query: string, limit: number = 50): Promise<SmartSearchResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/search/smart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, limit }),
      });

      if (!response.ok) {
        throw new Error(`Smart search failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      debug.error('Provider', 'Smart search error', error);
      throw new Error('Smart search failed. Please try again.');
    }
  }

  /**
   * Unified search — auto-detects query type (name, city/state, ZIP, NL phrase)
   */
  async unifiedSearch(query: string, limit: number = 50): Promise<SmartSearchResponse> {
    try {
      const params = new URLSearchParams({ q: query, limit: limit.toString() });
      const response = await fetch(`${this.baseUrl}/search/unified?${params}`);

      if (!response.ok) {
        // Fall back to smart search POST if unified endpoint not deployed yet
        return this.smartSearch(query, limit);
      }

      return await response.json();
    } catch (error) {
      debug.error('Provider', 'Unified search error, falling back to smart search', error);
      return this.smartSearch(query, limit);
    }
  }
}

interface BrowseResponse {
  providers: Provider[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
  categories: Array<{ name: string; count: number }>;
  coverage_summary: Record<string, number>;
}

interface CategoriesResponse {
  organization_types: Array<{ name: string; count: number }>;
  service_categories: Array<{ name: string; count: number }>;
  states: Array<{ state: string; count: number }>;
  data_sources: Array<{ data_source: string; count: number }>;
  total_providers: number;
}

interface SmartSearchResponse {
  providers: Provider[];
  total_count: number;
  search_time_ms: number;
  interpreted_query: Record<string, any>;
  suggestions: string[];
}

// Export singleton instance
export const providerDirectoryAPI = new ProviderDirectoryAPI();

// Export types
export type { SearchFilters, GeocodeResult, SearchResponse, BrowseResponse, CategoriesResponse, SmartSearchResponse };
